import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';

interface PaymentRow {
  id: string;
  bookingCode: string;
  customerName: string;
  tripName: string;
  travelDate: string;
  travelerCount: number;
  bookingTotal: number;
  payerName: string;
  paymentType: string;
  amount: number;
  paidAt: string;
  paidTime: string;
  slipFileName: string;
  receiptUrl: string;
  quotationUrl: string;
  status: string;
}
const PAYMENTS: PaymentRow[] = [
  { id: '1', bookingCode: 'BK-2026-000001', customerName: 'บริษัท สยามท่องเที่ยว จำกัด', tripName: 'ญี่ปุ่น โตเกียว ฟูจิ 6 วัน 4 คืน', travelDate: '2026-09-10', travelerCount: 4, bookingTotal: 100000, payerName: 'บริษัท สยามท่องเที่ยว จำกัด', paymentType: 'โอนเงินผ่านธนาคาร', amount: 50000, paidAt: '2026-08-08', paidTime: '10:30', slipFileName: 'slip-BK-2026-000001.jpg', receiptUrl: '', quotationUrl: '', status: 'WAITING_VERIFY' },
  { id: '2', bookingCode: 'BK-2026-000002', customerName: 'คุณพิมพ์ชนก แสนดี', tripName: 'เกาหลี โซล นามิซอม 5 วัน 3 คืน', travelDate: '2026-10-02', travelerCount: 2, bookingTotal: 68500, payerName: 'คุณพิมพ์ชนก แสนดี', paymentType: 'พร้อมเพย์', amount: 68500, paidAt: '2026-08-09', paidTime: '14:15', slipFileName: 'slip-BK-2026-000002.png', receiptUrl: 'https://example.com/receipts/RC-2026-000002', quotationUrl: 'https://example.com/quotations/QT-2026-000002', status: 'VERIFIED' },
];

@Component({
  selector: 'app-payments-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    StatusBadge,
    TableModule,
    SelectModule,
    DatePickerModule,
    InputTextModule,
  ],
  templateUrl: './payments-page.component.html',
})
export class PaymentsPageComponent {
  private readonly overlay = inject(GlobalOverlayService);
  readonly payments = signal(PAYMENTS);
  readonly selectedPayment = signal<PaymentRow | null>(null);
  readonly statusOptions = [
    { label: 'รอตรวจสอบ', value: 'WAITING_VERIFY' },
    { label: 'ตรวจสอบแล้ว', value: 'VERIFIED' },
    { label: 'ไม่ผ่าน', value: 'REJECTED' },
  ];
  selectedStatus: string | null = null;
  selectedDate: Date | null = null;

  toDateFilter(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  openPaymentDetail(row: PaymentRow): void {
    this.selectedPayment.set(row);
  }

  closePaymentDetail(): void {
    this.selectedPayment.set(null);
  }

  async approvePayment(row: PaymentRow): Promise<void> {
    const confirmed = await this.overlay.confirm({ title: 'ยืนยันยอดชำระ', message: `ยืนยันยอด ${row.amount.toLocaleString()} บาท ของ ${row.bookingCode} หรือไม่` });
    if (!confirmed) return;
    this.updatePaymentStatus(row.id, 'VERIFIED');
    this.closePaymentDetail();
    this.overlay.success('อนุมัติการชำระเงินสำเร็จ');
  }

  async rejectPayment(row: PaymentRow): Promise<void> {
    const confirmed = await this.overlay.confirm({ title: 'ไม่อนุมัติการชำระเงิน', message: `ยืนยันไม่อนุมัติยอด ${row.amount.toLocaleString()} บาท ของ ${row.bookingCode} หรือไม่` });
    if (!confirmed) return;
    this.updatePaymentStatus(row.id, 'REJECTED');
    this.closePaymentDetail();
    this.overlay.success('บันทึกการไม่อนุมัติเรียบร้อยแล้ว');
  }

  private updatePaymentStatus(id: string, status: string): void {
    this.payments.update((payments) =>
      payments.map((payment) => payment.id === id ? { ...payment, status } : payment),
    );
  }
}
